/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';

export const MARKET_DISCRIMINATOR = new Uint8Array([
  219, 190, 213, 55, 0, 227, 198, 154,
]);

export function getMarketDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(MARKET_DISCRIMINATOR);
}

export type Market = {
  discriminator: ReadonlyUint8Array;
  /** The oracle that determines the winner */
  oracle: Address;
  /** Quote mint for this market (e.g., USDC) */
  quoteMint: Address;
  /** The pot vault holding quote tokens */
  potVault: Address;
  /** Total quote migrated into pot (cumulative) */
  totalPot: bigint;
  /** Total quote claimed/paid out (cumulative) */
  totalClaimed: bigint;
  /** Winner mint (set when market resolves) */
  winnerMint: Address;
  /** Claimable supply of winner token (fixed after winning entry migrates) */
  claimableSupply: bigint;
  /** Whether the market has been resolved (winner determined) */
  isResolved: boolean;
  /** Bump seed for this PDA */
  bump: number;
  /** Bump seed for market authority PDA */
  marketAuthorityBump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

export type MarketArgs = {
  /** The oracle that determines the winner */
  oracle: Address;
  /** Quote mint for this market (e.g., USDC) */
  quoteMint: Address;
  /** The pot vault holding quote tokens */
  potVault: Address;
  /** Total quote migrated into pot (cumulative) */
  totalPot: number | bigint;
  /** Total quote claimed/paid out (cumulative) */
  totalClaimed: number | bigint;
  /** Winner mint (set when market resolves) */
  winnerMint: Address;
  /** Claimable supply of winner token (fixed after winning entry migrates) */
  claimableSupply: number | bigint;
  /** Whether the market has been resolved (winner determined) */
  isResolved: boolean;
  /** Bump seed for this PDA */
  bump: number;
  /** Bump seed for market authority PDA */
  marketAuthorityBump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link MarketArgs} account data. */
export function getMarketEncoder(): FixedSizeEncoder<MarketArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['oracle', getAddressEncoder()],
      ['quoteMint', getAddressEncoder()],
      ['potVault', getAddressEncoder()],
      ['totalPot', getU64Encoder()],
      ['totalClaimed', getU64Encoder()],
      ['winnerMint', getAddressEncoder()],
      ['claimableSupply', getU64Encoder()],
      ['isResolved', getBooleanEncoder()],
      ['bump', getU8Encoder()],
      ['marketAuthorityBump', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 29)],
    ]),
    (value) => ({ ...value, discriminator: MARKET_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link Market} account data. */
export function getMarketDecoder(): FixedSizeDecoder<Market> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['oracle', getAddressDecoder()],
    ['quoteMint', getAddressDecoder()],
    ['potVault', getAddressDecoder()],
    ['totalPot', getU64Decoder()],
    ['totalClaimed', getU64Decoder()],
    ['winnerMint', getAddressDecoder()],
    ['claimableSupply', getU64Decoder()],
    ['isResolved', getBooleanDecoder()],
    ['bump', getU8Decoder()],
    ['marketAuthorityBump', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 29)],
  ]);
}

/** Gets the codec for {@link Market} account data. */
export function getMarketCodec(): FixedSizeCodec<MarketArgs, Market> {
  return combineCodec(getMarketEncoder(), getMarketDecoder());
}

export function decodeMarket<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<Market, TAddress>;
export function decodeMarket<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<Market, TAddress>;
export function decodeMarket<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<Market, TAddress> | MaybeAccount<Market, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getMarketDecoder(),
  );
}

export async function fetchMarket<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<Market, TAddress>> {
  const maybeAccount = await fetchMaybeMarket(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeMarket<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<Market, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeMarket(maybeAccount);
}

export async function fetchAllMarket(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<Market>[]> {
  const maybeAccounts = await fetchAllMaybeMarket(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeMarket(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<Market>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeMarket(maybeAccount));
}

export function getMarketSize(): number {
  return 192;
}
